import { ReactNode, useRef } from 'react';

import { Box, Flex, Heading, List, ListIcon, ListItem, Skeleton } from '@chakra-ui/react';
import { useResizeObserver } from '@react-hookz/web';
import { CheckedIcon } from '@udacity/chakra-uds-icons';

import { CouponAppliedTag } from '~/components/coupon-applied-tag';
import { Markdown } from '~/components/markdown';
import { PlanMetadata } from '~/features/plans/models/plan-metadata';

import { useStandaloneProgramPrice } from '../../hooks/use-standalone-program-price';

import { PlanPriceCardLayout } from './plan-price-card-layout';

export function StandalonePurchaseCard({
  metadata,
  signUpButtonSlot,
  footnoteSlot,
  nodeKey,
  setPriceHeight,
  syncRenderedPriceHeight = true,
}: {
  metadata: PlanMetadata;
  signUpButtonSlot: ReactNode;
  footnoteSlot?: ReactNode;
  nodeKey: string;
  /**
   * To align the CTAs across the cards, we need to get the height of the cards.
   */
  setPriceHeight: (newHeight: number) => void;
  /**
   * Boolean value to turn on and off the alignment of the CTAs across the cards.
   */
  syncRenderedPriceHeight?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const { data } = useStandaloneProgramPrice({ nodeKey });
  useResizeObserver(
    ref,
    (entry) => {
      const height = entry.target.getBoundingClientRect().height;
      setPriceHeight(height);
    },
    syncRenderedPriceHeight
  );

  const { couponApplied, couponPercentOff, displayPrice, displayPriceUnits, strikeThroughDisplayPrice } = data || {};

  function renderPrice() {
    if (!displayPrice && !displayPriceUnits)
      return (
        <Flex flexDirection='column' gap={2}>
          <Skeleton height='40px' width='221px' />
        </Flex>
      );
    else if (strikeThroughDisplayPrice) {
      return (
        <>
          <Flex alignItems='center' gap={2}>
            <Heading alignItems='center' as='p' display='flex' flexWrap='wrap' gap='4px' size='h2'>
              <Heading
                as='del'
                color='gray.600'
                position='relative'
                size='marketing-2xl'
                textDecor='none'
                sx={{
                  '&::after': {
                    content: '""',
                    backgroundColor: 'gray.600',
                    width: '100%',
                    position: 'absolute',
                    height: '.1em',
                    borderRadius: '.1em',
                    left: '-5%',
                    whiteSpace: 'nowrap',
                    display: 'block',
                    transform: 'translateY(-50%) rotate(-16deg)',
                    top: '50%',
                  },
                }}
              >
                {strikeThroughDisplayPrice}
              </Heading>
            </Heading>
            {couponApplied && typeof couponPercentOff === 'number' && <CouponAppliedTag value={couponPercentOff} />}
          </Flex>
          <Heading as='p' color='blue.800' fontWeight='500' size='marketing-4xl'>
            {displayPrice}{' '}
            <Heading as='span' size='marketing-lg'>
              {displayPriceUnits}
            </Heading>
          </Heading>
        </>
      );
    } else {
      return (
        <Heading as='p' color='blue.800' fontWeight='500' size='marketing-4xl'>
          {displayPrice}{' '}
          <Heading as='span' size='marketing-lg'>
            {displayPriceUnits}
          </Heading>
        </Heading>
      );
    }
  }

  return (
    <PlanPriceCardLayout>
      <Flex flex='1' flexDir='column' gap={6}>
        <Flex
          flexDir='column'
          gap={6}
          sx={{
            '&::after': {
              content: "''",
              borderBottom: '1px solid',
              borderBottomColor: 'gray.600',
              opacity: '0.15',
            },
          }}
        >
          <Box flexBasis='var(--max-price-height)' flexGrow='1' flexShrink='0'>
            <Flex ref={ref} flexDir='column' gap={6}>
              <Flex gap='8px' justifyContent='space-between'>
                <Box>
                  <Heading as='h3' display='inline-block' size='marketing-xl'>
                    {metadata.title}
                  </Heading>
                </Box>
              </Flex>

              <Box>{renderPrice()}</Box>
            </Flex>
          </Box>
          <Box>{signUpButtonSlot}</Box>
        </Flex>
        <Flex flex='1' flexDir='column' gap={4}>
          <List display='flex' flexDir='column' gap={2}>
            {metadata.features.map((feature, idx) => {
              return (
                <ListItem key={idx} display='flex'>
                  <ListIcon as={CheckedIcon} color='blue.500' height='24px' width='24px' />
                  <Markdown
                    markdownText={feature.label}
                    components={{
                      p: ({ children }) => (
                        <Heading as='p' fontFamily='body' size='marketing-md'>
                          {children}
                        </Heading>
                      ),
                    }}
                  />
                </ListItem>
              );
            })}
          </List>
          <Box mt='auto'>{footnoteSlot}</Box>
        </Flex>
      </Flex>
    </PlanPriceCardLayout>
  );
}
